/**
 * ============================================================================
 * ⭐⭐ WHAT THE RAIL IS POINTED AT — ONE FOCUS, FOUR PANELS
 * ============================================================================
 *
 * > Pete, 2026-09-10: *"we have today where you hover over the driver and the
 * > Attached Paperwork shows. Let add the same logic to the communications panel
 * > - it will show all the communcations for that driver card. Same idea of
 * > appointments - all communciations for that appointment in the Commuications
 * > Panel. We will need an ALL icon to reset it, next to the search icon."*
 *
 * > And for Labor: *"lets do the same thing for labor panel. Each driver,
 * > appointment and then a ALL icon to see all the labor again."*
 *
 * > And History: *"a new panel called History. Same concept as the others, when
 * > you hover a driver, it shows you the driver history. When you hover the
 * > appointment, it shows you the appointment history."*
 *
 * ---- ⭐ IT LATCHES. IT DOES NOT FOLLOW THE MOUSE -------------------------
 *
 * Pete chose this explicitly on 2026-09-10: hovering SETS the scope and it stays
 * until ALL is clicked.
 *
 * ⚠️ THE ASK FOR AN "ALL ICON TO RESET IT" IS ITSELF THE EVIDENCE. A scope that
 * released itself when the cursor left would never need a reset control — the
 * request for one is a statement that the scope outlives the hover.
 *
 * ⛔ THE LATCH LIVES IN `Calendar.tsx`, NOT HERE. This module is pure: it says
 * what a focus IS and whether a given row belongs to it. Nothing in here
 * subscribes to anything or remembers anything between calls.
 *
 * ---- ⛔⛔ A FILTER THAT FAILS OPEN SHOWS THE WRONG DRIVER'S MESSAGES -------
 *
 * ⚠️ THIS RAIL FACES A LOBBY. Every predicate below fails CLOSED: given a focus
 * it cannot evaluate, it returns `false` and the panel says why it is empty. The
 * opposite convention — "if I can't tell, show it" — reads as a working filter
 * and quietly attributes one man's texts to another man standing at the desk.
 *
 * ⭐ THE PANELS NEVER CALL THESE WITH A NULL FOCUS. "No focus" means ALL, which
 * is the unfiltered list, and that decision is made before these run.
 * ============================================================================
 */

import type {
    CommunicationRowDto, LabourSessionDto, OrdersPanelRowDto,
} from '@/api/client';
import type { CheckInDto, AppointmentDto } from '@/api/types';
/* ⭐ ONE SHORT-NAME RULE, NOT TWO. `shortDriverName` lives in `panelSubject` because the
   header needed it first; the scope chip needs the SAME answer, so it imports rather than
   re-deriving. ⚠️ One-way dependency — `panelSubject` imports nothing, so no cycle.
     ** A SECOND IMPLEMENTATION OF A DISPLAY RULE IS TWO THINGS THAT MUST AGREE FOREVER. ** */
import { shortDriverName } from '@/lib/panelSubject';

/**
 * ⭐ WHAT THE CURSOR IS ON. Built by the two hover sources — the driver rail
 * (`CheckInPanel`) and the grid (`GanttCalendar`) — and consumed by four panels.
 *
 * ⚠️ `kind` IS NOT DERIVABLE FROM THE IDS. A driver matched to a load carries
 * BOTH a `checkInId` and an `appointmentId`, and so does an appointment whose
 * truck has arrived. What differs is what the scheduler pointed at, and that is
 * the thing the History panel has to know: pointing at the man asks for HIS
 * visits, pointing at the load asks for the LOAD's timeline.
 */
export interface RailFocus {
    /**
     * ⭐⭐ FOUR KINDS, TWO SHAPES. `driver` and `appointment` point at ONE truck;
     * `client` and `carrier` point at a PARTY and cover every appointment they
     * have today (decision 188, §1AV.171).
     *
     * > Pete, 2026-09-19: *"when we select a client or carrier in the Driver
     * > Checkin panel, it automatically pulls the WMS orders in the right panel
     * > correct?"* — ⛔ MEASURED: it did not. `clientFilter`/`carrierFilter` were a
     * LOCAL LIST FILTER and never touched the rail. This is what makes it true.
     */
    kind: 'driver' | 'appointment' | 'client' | 'carrier' | 'consignee';
    /** ⛔ Null when the focus came from the grid and no truck has arrived. */
    checkInId: string | null;
    /** ⛔ Null for a driver who has not been matched to a load yet. */
    appointmentId: string | null;
    appointmentNumber: string | null;
    /**
     * ⭐⭐ THE LAST FOUR DIGITS, AND THE ONLY HANDLE ON AN UNMATCHED DRIVER.
     *
     * > Pete, 2026-09-10: *"Comms can be sent to a driver after he checks in, no
     * > matter if he is assoicated to an appointment yet."*
     *
     * ⛔ HE IS RIGHT, AND IT NEARLY SHIPPED WRONG. `CommunicationRowDto` carries
     * `appointmentId` and no `checkInId`, so the obvious reading is that an
     * unmatched driver simply cannot be scoped. ⚠️ But the messages ARE there —
     * they just have a null appointment — and the recipient string is already
     * masked to name plus last four ("Tony ····4417") for exactly this rail.
     *
     * ⭐ SO THE TAIL IS THE JOIN. It matches what is ON SCREEN, which is the same
     * rule the panel's own search box has followed since 2026-08-27, rather than
     * a second identity scheme nobody can see the workings of.
     */
    phoneTail: string | null;
    driverName: string | null;
    /**
     * ⭐⭐ THE LOAD'S OWN NUMBERS — PO, reference, container — for the WMS Orders
     * panel. Whatever the arrival actually carries; empty is normal and legal.
     *
     * ⛔ RAW, NOT NORMALISED. `normaliseIdentifier` runs at comparison time on
     * BOTH sides, so there is one rule in one place. Pre-normalising here would
     * put a second, invisible copy of that rule in every caller.
     */
    identifiers: string[];
    /**
     * ⭐⭐⭐ EVERY APPOINTMENT THIS FOCUS COVERS — THE ONE IDEA THAT MADE PARTIES
     * WORK WITHOUT REWRITING EVERY MATCHER.
     *
     * ⛔ The matchers used to compare `f.appointmentId` as a single value, which
     * cannot express "this client's eleven loads". Widening the COMPARISON rather
     * than adding a parallel code path means a driver focus is simply **a set of
     * one**, and Communications, Labour, Orders and History all scope to a party
     * with no branch on `kind` anywhere.
     *
     *   ** WHEN A NEW CASE NEEDS "ANY OF THESE" AND THE OLD CASE SAYS "THIS ONE",
     *      THE OLD CASE IS A SET OF ONE. ADDING A SECOND CODE PATH INSTEAD IS HOW
     *      THE TWO DRIFT. **
     *
     * ⚠️ `appointmentId` STAYS, and stays singular, because the labels and the
     * blocked-reasons speak about one load. It is null for a party focus.
     */
    appointmentIds: string[];
    /** ⛔ The client or carrier code. Null for a driver or appointment focus. */
    partyCode: string | null;
    /** What the party is called on screen. Null unless `kind` is client/carrier. */
    partyName: string | null;
    /** ⭐ What the panel headers say. Built by the caller that knows the context. */
    label: string;
}

/** ⭐ Drops blanks and duplicates so a load with three copies of one PO reads as one. */
function identifierList(...raw: (string | null | undefined)[]): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const v of raw) {
        const t = v?.trim();
        if (!t) continue;
        const key = normaliseIdentifier(t);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(t);
    }
    return out;
}

/**
 * ---- ⛔⛔ AN ARRIVAL WITH NO LOAD IS STILL A FOCUS -------------------------
 *
 * > Pete, 2026-09-10: *"Comms can be sent to a driver after he checks in, no
 * > matter if he is assoicated to an appointment yet."*
 *
 * ⛔ THIS IS THE BUG THIS BUILDER EXISTS TO FIX. The old hover emitted
 * `onHoverCardShown(null)` for an arrival with no `appointmentId`, because its
 * only consumer was Attached Paperwork and paperwork genuinely needs a load.
 * ⚠️ Reusing that shape for four more panels would have carried a paperwork
 * limitation into Communications and History, where it is simply wrong — his
 * texts and his previous visits both exist before he is matched to anything.
 *
 * ⭐ SO A CHECK-IN ALWAYS PRODUCES A FOCUS. Whether any given PANEL can scope it
 * is that panel's question, answered by `focusBlockedReason`.
 */
export function focusFromCheckIn(c: CheckInDto): RailFocus {
    /**
     * ⭐ THE MAN'S NAME — OR HIS PHONE WHEN HE HAS NOT GIVEN ONE. The same rule
     * the Attached Paperwork label has used since 2026-09-10; Pete's own test
     * card reads "Awaiting driver name", so the number is frequently the only
     * thing that identifies him.
     */
    const who = c.driverName?.trim() || c.driverPhoneEntered?.trim() || null;
    return {
        kind: 'driver',
        checkInId: c.checkInId,
        appointmentId: c.appointmentId,
        /* ⭐ A SET OF ONE — or of none, for a driver with no load yet. */
        appointmentIds: c.appointmentId ? [String(c.appointmentId)] : [],
        partyCode: null,
        partyName: null,
        appointmentNumber: c.appointmentNumber,
        phoneTail: c.driverPhoneEntered,
        driverName: c.driverName?.trim() || null,
        /* ⚠️ `trailerNumber` IS INCLUDED HERE AND NOT ON THE APPOINTMENT, because
           AppointmentDto deliberately has no such field (see its note) while a
           check-in records what actually rolled up to the gate. */
        identifiers: identifierList(
            c.poNumber, c.referenceNumber, c.containerNumber, c.trailerNumber),
        label: c.appointmentNumber
            ? (who ? `${c.appointmentNumber} · ${who}` : c.appointmentNumber)
            : (who ?? 'Arrival'),
    };
}

/**
 * ⭐ THE LOAD, POINTED AT DIRECTLY. `label` is supplied by the caller because
 * `Calendar` already composes the good one — number plus door plus time, which is
 * what a person on a dock recognises a load by (2026-09-10). ⛔ Recomposing it
 * here would be a second source of truth for the same sentence.
 */
export function focusFromAppointment(
    a: AppointmentDto,
    label: string,
    /**
     * ⭐⭐ WHO IS DRIVING IT, WHEN THE PAGE KNOWS (Pete, 2026-09-20).
     *
     * ⭐ *"I guess we don't have a way to see the drivers to specific clients or specific
     * carriers right? When we are on the client or carriers tab, even if we click the
     * appointment, we dont see the driver."*
     *
     * ⛔⛔ IT WAS HARD-CODED `null`, so `panelTitle` fell back to the label and line 2 read
     * `Load · A00000326 · DD120 · 12:00 PM` — the door and the time, which the line above
     * already implies, and never the one fact the other chips cannot show you.
     *   ** A FIELD SET TO `null` BECAUSE THE FIRST CALLER HAD NOTHING TO PUT IN IT IS A
     *      FIELD EVERY LATER CALLER BELIEVES IS UNAVAILABLE. **
     *
     * ⚠️ OPTIONAL, AND THE CALLER RESOLVES IT. The name lives on the ARRIVAL, not on the
     * appointment, so only the page holding the check-in list can join the two — and a
     * load with no truck yet legitimately has none.
     */
    driverName: string | null = null,
): RailFocus {
    return {
        kind: 'appointment',
        checkInId: null,
        appointmentId: String(a.appointmentId),
        appointmentIds: [String(a.appointmentId)],
        partyCode: null,
        partyName: null,
        appointmentNumber: a.appointmentNumber,
        phoneTail: null,
        driverName,
        identifiers: identifierList(a.poNumber, a.referenceNumber, a.containerNumber),
        label,
    };
}

/**
 * ---- ⭐⭐⭐ AN **ORDER'S** LOADS — THE WMS PANEL'S FOCUS (2026-09-27) ----------
 *
 * > Pete: *"when you hover an appointment or hover the WMS orders record, it needs to
 * > highlight appointment or order record associated to it"* … *"when you click on an
 * > order or an appointment … the appointment only shows in the calendar and all the
 * > panels show the specific appointment information including the one order."*
 *
 * ⛔⛔ IT IS A **BUILDER**, NOT AN OBJECT LITERAL AT THE CALL SITE, and this file has
 * already paid for the alternative: the driver focuses were *"assembled inline here
 * and there was no way to run them"*. A focus is nine fields that four matchers read;
 * one built by hand in a panel is a focus nothing can test.
 *   ** A SHAPE THAT FOUR CONSUMERS DEPEND ON IS A FUNCTION, OR IT IS A CONVENTION
 *      NOBODY CAN VERIFY. **
 *
 * ---- ⭐⭐ ONE ORDER CAN BE ON SEVERAL TRUCKS, AND THAT IS NOT AN EDGE CASE -----
 *
 * Pete, 2026-09-09: *"an order can obviously have multiple trucks to fulfill it but
 * only 1 truck per appointment"*. So `appointmentIds` takes ALL of them — the same
 * field a party focus fills, and the reason `ordersMatchesFocus`, `labourMatchesFocus`
 * and `commsMatchesFocus` all read the array rather than one id.
 *
 * ⛔⛔ BUT THE **SINGULAR** FIELDS ARE SET ONLY WHEN THERE IS EXACTLY ONE. With two
 * trucks there is no single `appointmentId` and no single `appointmentNumber`, and
 * filling them from `[0]` would be a guess wearing the authority of a fact — the
 * paperwork tray would open on one truck of three, and `focusScopeLabel` would print
 * `(Appt - A00000406)` about a focus covering two others.
 *   ** WHEN A FIELD MEANS "THE ONE", HAVING SEVERAL MEANS IT HAS NO VALUE — NOT THAT
 *      ANY OF THEM WILL DO. **
 * ⭐ `focusScopeLabel` already degrades correctly: it falls back to `(Appointment)`
 * when there is no number, which is honest rather than arbitrary.
 *
 * ⚠️ AN UNBOOKED ORDER YIELDS A FOCUS THAT IS NOT SCOPEABLE — no appointments, no
 * phone, no check-in — and `focusIsScopeable` will say so. ⛔ It still returns one
 * rather than null, so the caller can show WHY the panels are empty instead of
 * silently doing nothing; that is the rule `focusBlockedReason` exists for.
 *
 * ⛔ NO IDENTIFIERS. A party focus pools PO / reference / container so the orders
 * panel can find its rows — but this focus CAME FROM an order, and feeding its own
 * numbers back would let it match sibling orders that merely share a container.
 * `ordersMatchesFocus` joins on `bookedOn` appointment ids, which is the bound that
 * cannot drift.
 *   ** A FOCUS BUILT FROM A ROW MUST NOT CARRY THE KEYS THAT WOULD RE-SELECT ITS
 *      NEIGHBOURS. **
 */
export function focusFromBookedOrder(
    /** The order number, for the label a scheduler reads. */
    orderNumber: string,
    /** Its bookings — `OrdersPanelRowDto.bookedOn`, in whatever order the server sent. */
    bookedOn: readonly { appointmentId: string; appointmentNumber: string | null }[],
): RailFocus {
    /* ⚠️ BLANKS DROPPED AND DUPLICATES COLLAPSED, like `identifierList` does. Two
       bookings on one appointment is not two trucks. */
    const ids = Array.from(new Set(
        bookedOn.map(b => String(b.appointmentId)).filter(Boolean),
    ));
    const only = ids.length === 1 ? bookedOn.find(b => String(b.appointmentId) === ids[0]) : undefined;
    return {
        kind: 'appointment',
        checkInId: null,
        appointmentId: only ? String(only.appointmentId) : null,
        appointmentIds: ids,
        partyCode: null,
        partyName: null,
        appointmentNumber: only ? only.appointmentNumber : null,
        phoneTail: null,
        driverName: null,
        identifiers: [],
        label: orderNumber,
    };
}

/**
 * ---- ⭐⭐ A WHOLE CLIENT OR CARRIER, AND EVERY LOAD THEY HAVE TODAY ----------
 *
 * > Pete, 2026-09-19: *"you could select a single client for example and just see
 * > there appointments in the schedule"* — and, on the rail: *"The client carrier
 * > selection drives the other rails in this logic too correct?"*
 *
 * ✅ It does, because the matchers now compare against `appointmentIds` rather than
 * one id. Communications, Labour, Orders and History all follow with no branch on
 * `kind`.
 * ⛔⛔ SCANNED PAPERWORK DOES NOT, and that is not an oversight: it is wired to
 * `attached?: AttachedTarget`, a different prop that REQUIRES one `appointmentId`,
 * and it has no `ALL` control. That is item 188a, tracked separately.
 *
 * ⚠️ IDENTIFIERS ARE POOLED ACROSS THE PARTY'S LOADS. The Orders panel matches on
 * PO / reference / container, so a client focus should find the orders behind any
 * of today's loads. `identifierList` already drops blanks and duplicates.
 * ⛔ NO PHONE TAIL. A party is not a person and has no number to match messages by;
 * leaving it null keeps `commsMatchesFocus` on the appointment join, which is the
 * only honest basis here.
 */
export function focusFromParty(
    /* ⚠️ CONSIGNEE JOINED 2026-09-19 and behaves identically HERE — a set of the
       party's appointments. ⛔ The CALLER's job is harder: an appointment has MANY consignees
       (`dock.AppointmentConsignee` is a join table), so the same appointment
       legitimately appears in several consignee focuses and the per-consignee counts
       do not sum to the day's trucks. See `lib/panelSubject.ts`. */
    kind: 'client' | 'carrier' | 'consignee',
    code: string,
    name: string | null,
    appointments: readonly AppointmentDto[],
): RailFocus {
    const ids = appointments.map(a => String(a.appointmentId));
    return {
        kind,
        checkInId: null,
        /* ⛔ NULL, DELIBERATELY. The singular field means "the one load this focus is
           about", and a party focus is about none of them in particular. Setting it
           to the first would make every label and blocked-reason speak about an
           arbitrary load. */
        appointmentId: null,
        appointmentNumber: null,
        appointmentIds: ids,
        partyCode: code,
        partyName: name?.trim() || code,
        phoneTail: null,
        driverName: null,
        identifiers: identifierList(
            ...appointments.flatMap(a => [a.poNumber, a.referenceNumber, a.containerNumber])),
        label: name?.trim() || code,
    };
}

/**
 * ⛔ A FOCUS THAT CANNOT SCOPE ANYTHING IS NOT A FOCUS./**
 * ⛔ A FOCUS THAT CANNOT SCOPE ANYTHING IS NOT A FOCUS. Guards the case where a
 * driver has checked in with neither a load nor a phone number — rare, but it
 * happens at a kiosk, and without this the panels would latch onto a scope that
 * matches nothing and read as "no messages were sent to this man".
 */
export function focusIsScopeable(f: RailFocus): boolean {
    /* ⭐ `checkInId` JOINED THIS LIST 2026-09-10, once the server began recovering
       'checkin:' dedup keys. ⚠️ It means a driver with NO load and NO phone on file
       can still be scoped — his messages are found by the visit rather than by
       anything about him. ⛔ Older rows predate the id, which is why the phone tail
       stays in the list rather than being replaced by it. */
    return f.appointmentIds.length > 0 || Boolean(f.phoneTail) || Boolean(f.checkInId);
}

/**
 * ⭐ NORMALISE A RECIPIENT STRING FOR COMPARISON. Lower-cased, and the mask
 * characters dropped so "Tony ····4417" and "Tony 4417" compare equal.
 *
 * ⚠️ THE MASK GLYPH IS U+00B7 (MIDDLE DOT), not a full stop and not an ellipsis.
 * A regex written against "..." matches nothing here and the filter silently
 * returns empty — which looks exactly like a driver who was never texted.
 */
function normaliseRecipient(raw: string): string {
    return raw.toLowerCase().replace(/[·.…]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * ⭐ JUST THE DIGITS, so a tail stored as "x0484" or "(312) 952-0484" both reduce
 * to something comparable. Returns the LAST FOUR, which is all the server sent.
 */
export function tailDigits(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const digits = raw.replace(/\D+/g, '');
    return digits.length >= 4 ? digits.slice(-4) : null;
}

/**
 * ---- ⛔⛔ DOES THIS MESSAGE BELONG TO WHAT THE CURSOR IS ON? ---------------
 *
 * TWO WAYS IN, AND THE SECOND ONE IS THE ONE PETE ASKED FOR:
 *
 *   1. THE LOAD MATCHES. Unambiguous — an id compared to an id.
 *   2. THE RECIPIENT'S TAIL MATCHES. This is how a driver with no load is found.
 *
 * ---- ⚠️ TWO DRIVERS CAN SHARE A LAST FOUR ---------------------------------
 *
 * ⛔ SO THE NAME IS REQUIRED TOO WHENEVER BOTH SIDES HAVE ONE. A bare tail match
 * would need only a 1-in-10,000 coincidence inside a 50-row window to file one
 * man's texts under another's name; requiring the first name as well means a
 * collision needs the same four digits AND the same name.
 *
 * ⚠️ AND THE NAME IS OFTEN ABSENT — Pete's own test card reads "Awaiting driver
 * name". ⭐ When the focus has no name the tail alone is accepted, because the
 * alternative is showing nothing for the very drivers this feature exists for.
 * The panel header names the basis it matched on, so a wrong hit is visible
 * rather than merely wrong.
 *
 * ⛔ THE TAIL IS COMPARED AGAINST `recipients` ONLY — never against the row's
 * whole searchable text. "0484" occurs inside appointment numbers, PO numbers
 * and container numbers, and a haystack match would pull in loads that have
 * nothing to do with the man.
 */
export function commsMatchesFocus(row: CommunicationRowDto, f: RailFocus): boolean {
    /* ⛔ FAIL CLOSED. See the header. */
    if (!focusIsScopeable(f)) return false;

    /**
     * ⭐⭐ THE ARRIVAL, EXACTLY (2026-09-10). Added once `dbo.NotificationSent`'s
     * 'checkin:' dedup key was being recovered server-side — an id against an id,
     * which needs no name tie-breaker and cannot confuse two drivers who share a
     * last four. ⛔ IT IS TRIED FIRST for that reason.
     *
     * ⚠️ THE TAIL MATCH BELOW IS NOT DEAD CODE. Rows written before this shipped
     * carry no `checkInId`, and the field is optional precisely so an older
     * payload still renders — so the heuristic remains the fallback, not the
     * primary. Deleting it would blank the panel for anything already sent.
     */
    if (f.checkInId && row.checkInId
        && String(row.checkInId) === String(f.checkInId)) {
        return true;
    }

    if (row.appointmentId && f.appointmentIds.includes(String(row.appointmentId))) {
        return true;
    }

    /* ⭐ An APPOINTMENT focus stops here. Pointing at a load asks for that load's
       messages; reaching on to the driver's other trips would answer a question
       nobody asked and would cross appointments. */
    if (f.kind === 'appointment') return false;

    const tail = tailDigits(f.phoneTail);
    if (!tail) return false;

    const wantName = f.driverName?.trim().toLowerCase() || null;
    return (row.recipients ?? []).some(r => {
        const norm = normaliseRecipient(r);
        /* ⛔ THE TAIL MUST BE THE END OF A DIGIT RUN, not a substring. A masked
           number rendered as "····44170" (a five-digit tail, which a future
           masking change could produce) must not match "4417". */
        if (!new RegExp(`(?:^|\\D)${tail}(?:\\D|$)`).test(norm)) return false;
        if (!wantName) return true;
        /* ⚠️ FIRST NAME ONLY. The server sends what it has, and "Tony" against
           "Tony Marchetti" must not be a miss. */
        const first = wantName.split(/\s+/)[0];
        return first.length > 0 && norm.includes(first);
    });
}

/**
 * ---- ⭐ LABOUR IS ABOUT A TRUCK AT A DOOR ---------------------------------
 *
 * ⛔ `LabourSessionDto` CARRIES `appointmentId` AND NOTHING ELSE THAT IDENTIFIES
 * A PERSON — `workerName` is the WAREHOUSE worker, not the driver. ⚠️ Reading it
 * as the driver would attribute a lumper's shift to whichever driver happened to
 * share his first name, which is worse than showing nothing.
 *
 * ⭐ SO A DRIVER FOCUS SCOPES LABOUR ONLY THROUGH HIS LOAD. An unmatched driver
 * has no labour view, and the panel says so rather than showing an empty list.
 */
export function labourMatchesFocus(s: LabourSessionDto, f: RailFocus): boolean {
    /* ⛔⚤ ~~if (f.appointmentIds.length === 0) return false;~~ — DELETED. It read
       as a guard and was dead: `includes` on an empty array is already false. A
       mutation removing it changed nothing, which is how it was found.
       ** PRESENCE IS NOT EFFECT. ** The predecessor `if (!f.appointmentId)` WAS
       load-bearing, because the line under it compared a single value; widening
       the comparison to a set retired the guard and I kept it out of habit. */
    return Boolean(s.appointmentId) && f.appointmentIds.includes(String(s.appointmentId));
}

/**
 * ---- ⭐ ONE NUMBER, WRITTEN FOUR WAYS -------------------------------------
 *
 * ⛔ WMS NUMBERS ARRIVE PUNCTUATED INCONSISTENTLY. The same container is
 * "MSCU-123456-7" on the appointment and "MSCU1234567" in the order cache, and
 * the Orders panel's own search already declares columns `separatorInsensitive`
 * for exactly this reason. ⚠️ A plain `===` here would silently find nothing and
 * read as "this load has no order", which is the most expensive wrong answer
 * this panel can give.
 *
 * ⭐ UPPER-CASED AND STRIPPED TO ALPHANUMERICS, both sides, at compare time.
 * Returns null for anything with no alphanumeric content, so a stray "-" never
 * becomes a key that matches another stray "-".
 */
export function normaliseIdentifier(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const n = raw.toUpperCase().replace(/[^A-Z0-9]+/g, '');
    return n.length > 0 ? n : null;
}

/**
 * ---- ⭐⭐ WHICH WMS ORDERS BELONG TO THE LOAD THE CURSOR IS ON -------------
 *
 * > Pete, 2026-09-10: *"We should also search the WMS Orders just like all the
 * > others, the information from the appointment searched for any matching
 * > orders in the WMS."* And then: *"Only search whats loaded on in the WMS
 * > panel."*
 *
 * ⛔⛔ SO THIS IS A FILTER, NOT A QUERY. It never re-asks the WMS. The panel's
 * loaded page is the population, exactly as Pete chose for Communications.
 * ⚠️ WHICH MEANS AN ORDER OUTSIDE THE LOADED RANGE CANNOT BE FOUND, and the
 * panel must say the scope is "within what is loaded" — otherwise an order that
 * is simply on another page reads as an order that does not exist.
 *
 * ---- ⛔⛔ THIS FUNCTION USED TO MATCH NUMBERS ITSELF, AND IT WAS WRONG -----
 *
 * It had two branches: `bookedOn` containing the appointment (an id against an
 * id), and — failing that — its own comparison of the load's numbers against
 * the order's. The second branch was a FIFTH expression of the appointment ↔
 * order match rule, and it disagreed with the server in two ways:
 *
 *   · IT STRIPPED TO ALPHANUMERICS (`[^A-Z0-9]+`). The server strips five named
 *     separators (`- _ space . /`). So `MSCU#1234` matched here and did not in
 *     the database.
 *   · IT COMPARED ANY IDENTIFIER TO ANY IDENTIFIER — one set of the load's
 *     numbers against all six of the order's — so a PO number could match a PRO
 *     number. SPEC §3.2 says which field may equal which, and it may not.
 *
 *   ⛔ TWO DEFINITIONS OF ONE QUESTION DO NOT DISAGREE UNTIL SOMEBODY NOTICES.
 *      THEY DISAGREE FROM THE DAY THE SECOND ONE IS WRITTEN.
 *
 * ⭐ THE SERVER DECIDES NOW (Pete, 2026-09-26). `bl` in `ordersPanelRepository`
 * evaluates `buildAppointmentOrderMatchSql` — the one rule — and every
 * appointment it matched comes back in `bookedOn`, each with the `matchedOn`
 * label that says WHICH pair explained it. So an id against an id is no longer
 * the weaker of two tests; it is the whole test, because the ids in that list
 * were put there by the authoritative rule.
 *
 *   ⭐ A SCREEN THAT RE-DERIVES WHAT THE SERVER ALREADY DECIDED IS NOT BEING
 *      CAREFUL. IT IS CASTING A SECOND VOTE.
 *
 * ⚠️ THIS DEPENDS ON `bookedOn` BEING COMPLETE, which is why `MAX_BOOKINGS_LISTED`
 * exists in the repository. That list was `TOP 3` — a width for the rail — until
 * this function started reading it.
 *
 * ⚠️ STILL A FILTER, NOT A QUERY. It never re-asks the WMS; the panel's loaded
 * page is the population, exactly as Pete chose for Communications. An order
 * outside the loaded set cannot be found here, and the panel must say the scope
 * is "within what is loaded" — otherwise an order that is merely on another page
 * reads as an order that does not exist.
 *
 * ⚠️ AND IT STILL FAILS CLOSED. A focus with no appointment ids matches nothing
 * rather than everything.
 */
export function ordersMatchesFocus(row: OrdersPanelRowDto, f: RailFocus): boolean {
    return (row.bookedOn ?? []).some(b => f.appointmentIds.includes(String(b.appointmentId)));
}

/**
 * ⭐⭐ WHY A SCOPED PANEL IS EMPTY — IN WORDS, NEVER AS A BLANK LIST.
 *
 * > Pete chose "Say why it's empty" on 2026-09-10, over falling back to ALL.
 *
 * ⛔ AN EMPTY LIST HAS THREE CAUSES AND THEY NEED DIFFERENT ANSWERS: nothing was
 * sent, nothing is in the loaded window, or this focus cannot be scoped at all.
 * ⚠️ A panel that renders the same emptiness for all three is how somebody
 * concludes a driver was never contacted and texts him twice.
 *
 * Returns null when the panel is scopeable and simply had no rows — the caller
 * then uses its own ordinary empty state.
 */
export function focusBlockedReason(
    f: RailFocus, panel: 'comms' | 'labour' | 'orders',
): string | null {
    if (panel === 'labour' && f.appointmentIds.length === 0) {
        if (f.kind === 'driver') return 'Not matched to an appointment yet — labor is tracked against the appointment.';
        /* ⛔ A PARTY WITH NO APPOINTMENTS TODAY IS A DIFFERENT SENTENCE from an
           appointment with no id. Saying "this appointment has no id" about a client
           reads as a data fault in a record the scheduler never asked about. */
        if (f.kind === 'client' || f.kind === 'carrier') {
            return `No appointments for ${f.partyName ?? 'this party'} today — nothing to track labor against.`;
        }
        /* ⛔⛤ ~~'This appointment has no load id.'~~ — 2026-09-25. The sweep
           renamed the DRIVER branch above and left this one, so one function said
           "appointment" in its first sentence and "load id" in its third.
             ** A RENAME APPLIED TO SOME BRANCHES OF ONE FUNCTION IS WORSE THAN NO
                RENAME: IT TEACHES THAT THE TWO WORDS MEAN DIFFERENT THINGS. **
           rail-focus-test.cjs is what caught it, by failing on a needle aimed at
           the branch that HAD been renamed. */
        return 'This appointment has no id to track labor against.';
    }
    if (panel === 'comms' && !focusIsScopeable(f)) {
        return 'No appointment and no phone number on this arrival — nothing to match messages against.';
    }
    /* ⛔ THE ORDERS PANEL NEEDS AN APPOINTMENT, NOT A PHONE — AND, SINCE
       2026-09-26, NOT NUMBERS EITHER. A driver's tail says nothing about which
       order is his, so a focus that Communications can scope perfectly well may
       still have nothing for this panel.

       ⛔⛤ THE OLD CONDITION ALSO REQUIRED THAT EVERY IDENTIFIER BE UNUSABLE,
       because `ordersMatchesFocus` had a second branch that compared numbers
       itself. That branch is gone — the server decides the match now — so a
       focus with numbers but no appointment id can no longer match anything,
       and saying "no PO, reference or container" about it would name the wrong
       missing thing.
         ** WHEN A FUNCTION STOPS READING A FIELD, EVERY SENTENCE THAT EXPLAINED
            THAT FIELD IS NOW A LIE IN WAITING. **

       ⚠️ AND THE WORDING CHANGED WITH IT. `tools/rail-focus-test.cjs` asserts on
       these strings and is expected to fail until its needle is updated — that
       failure is the guard doing its job, not a break. */
    if (panel === 'orders' && f.appointmentIds.length === 0) {
        return 'Not matched to an appointment yet — WMS orders are matched through the appointment.';
    }
    return null;
}

/**
 * ⭐ WHAT THE PANEL HEADER SAYS IT MATCHED ON, so a wrong hit is visible.
 *
 * ⚠️ THIS IS NOT DECORATION. The tail match is a heuristic (see
 * `commsMatchesFocus`), and a heuristic that does not show its working is
 * indistinguishable from a database join when it is right and invisible when it
 * is wrong.
 */
/**
 * ---- ⭐⭐ THE RAIL'S SCOPE LABEL — ALWAYS PRESENT (increment 3) ----------------
 *
 * ⭐ Pete: *"Lets put a little (client) or (carrier) underneath the panel title … If
 * other maybe it says (ALL)."*
 *
 * ⛔⛔ A LABEL AND A RESET ARE DIFFERENT KINDS OF THING AND GET OPPOSITE RULES:
 *   ** A LABEL IS AN INDICATOR AND MUST READ IN EVERY STATE; A RESET IS A CONTROL
 *      AND MUST NOT BE ON SCREEN WHEN IT WOULD DO NOTHING. **
 * So this returns `(ALL)` for a null focus rather than an empty string — the panel
 * renders it unconditionally and never has to decide.
 *
 * ⚠️ `RailFocus.kind` HAS FIVE VALUES AND PETE NAMED THREE. Decision 16 settled the
 * other two as **`(driver)` and `(load)`**, on the grounds that "load" was the word
 * this codebase and the dock already used ("Nothing filed on this load").
 *
 * ⛔⛤ ~~`appointment: 'load'`~~ — **DECISION 16 IS OVERTURNED, 2026-09-25**, by Pete,
 * asked directly because overturning a recorded decision is not a thing to do quietly:
 * *"Finish it — Appointment everywhere."*
 *
 * ⚠️ AND ITS REASONING IS WHAT EXPIRED, NOT ITS LOGIC. Decision 16 was right that a
 * label should use the word the building uses. The building's word changed: the
 * vocabulary Pete chose on 2026-09-24 is **Truck / Appointment / Order**, and "load"
 * was retired precisely because it meant the appointment in some strings and the WMS
 * order in others (see CheckInPanel's "Load details" and LabourPanel's booking note).
 *   ** A DECISION THAT CITES A FACT IS ONLY AS DURABLE AS THE FACT. WHEN THE FACT
 *      MOVES, RE-DECIDE — DO NOT DEFEND THE CONCLUSION. **
 */
const SCOPE_WORD: Readonly<Record<RailFocus['kind'], string>> = {
    driver: 'driver', appointment: 'appointment',
    client: 'client', carrier: 'carrier', consignee: 'consignee',
};

/**
 * ---- ⛔⛤ `Load · GEODIS` — THE TERNARY THAT PREDATED PARTIES (2026-09-19) ------
 *
 * ⭐ Pete: *"the client or carrier next to the right side panel is not getting
 * updated when a client or carrier is selected"*.
 *
 * ⛔ MEASURED: all four rail panels built that heading as
 * `focus.kind === 'driver' ? 'Driver' : 'Load'` — a two-way ternary written when
 * `RailFocus.kind` had two values. Parties added three more, and every one of them
 * fell through the `else` and was announced as a **Load**. A carrier focus therefore
 * read `Load · GEODIS`, which is not a stale render but a wrong word, in four files.
 *
 *   ** A TERNARY IS AN EXHAUSTIVE CLAIM ABOUT A TYPE. WIDEN THE TYPE AND IT DOES NOT
 *      BREAK — IT SILENTLY RE-LABELS EVERY NEW CASE AS THE `else`. **
 *   ⚠️ `tsc` CANNOT SEE THIS. A `Record<Kind, string>` can, which is why the map
 *   below is a map and not a chain: adding a sixth kind stops compiling.
 */
/* ⛔⛤ ~~`appointment: 'Load'`~~ — 2026-09-25, same overturn as SCOPE_WORD above.
   ⚠️ THIS ONE IS THE VISIBLE ONE. SCOPE_WORD.appointment is only a FALLBACK inside
   `focusScopeLabel` (the normal case renders `(Appt - A00000310)`), but FOCUS_NOUN is
   the rail panel heading on every appointment focus: `Load · A00000310` on screen all
   day. It survived the sweep because no guard named it, and no ternary was left to
   break — the map that fixed the `Load · GEODIS` bug also made the word invisible to
   everything except a reader.
     ** THE MAP THAT STOPPED A WRONG WORD FROM SPREADING ALSO STOPPED ANYONE FROM
        NOTICING IT WAS THE WRONG WORD. **
   rail-focus-test.cjs now pins all five, so the next rename cannot land on four. */
const FOCUS_NOUN: Readonly<Record<RailFocus['kind'], string>> = {
    driver: 'Driver', appointment: 'Appointment',
    client: 'Client', carrier: 'Carrier', consignee: 'Consignee',
};

export function focusNoun(f: RailFocus): string {
    return FOCUS_NOUN[f.kind];
}

/**
 * ---- ⭐⭐ IS THIS THE SAME SCOPE, OR A NARROWER ONE INSIDE IT? (Pete, 2026-09-19) ----
 *
 * ⭐ Pete: *"it is taking away the - Client line that will have the circle R. when
 * there is one client, carrier, consignee selected, this should go under that first
 * line so you still see the client, carrier, consignee name so you can distinguish"*.
 *
 * ⛔⛔ THE HEADER NOW SHOWS TWO SCOPES AT ONCE and has to tell them apart. The page
 * hands the rail `railFocus ?? partyScope` — ONE collapsed value — so when nothing is
 * hovered the "narrower" focus IS the party scope, and rendering it on a second line
 * would print the same sentence twice.
 *
 * ⚠️ NOT `===`. It happens to work today because the page passes the very same object
 * in both props, but that is an accident of how `??` is written, not a guarantee. A
 * memo, a spread, or a round-trip through `focusFromParty` would each produce an equal
 * focus with a different identity and the duplicate line would reappear — as a render
 * bug, with nothing in the code saying why.
 *   ** A PREDICATE THAT IS TRUE BY REFERENCE IS TRUE BY LUCK. COMPARE THE VALUES. **
 *
 * ⛔ THE FOUR FIELDS ARE THE IDENTITY OF A FOCUS. `label` is deliberately NOT among
 * them: it is display text (`A00000346 · DD122 · 1:00 PM`) and it changes when a door
 * is assigned or a time is moved, which would make an unchanged scope compare as new.
 */
export function sameFocus(a: RailFocus | null, b: RailFocus | null): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.kind === b.kind
        && a.partyCode === b.partyCode
        && a.appointmentId === b.appointmentId
        && a.checkInId === b.checkInId;
}

/**
 * ---- ⭐⭐ IT NAMES THE THING, NOT ITS KIND (Pete, 2026-09-20, decision 194) ----
 *
 * ⭐ Pete: *"The (client) next to the panels is not changing to (Traffix). And when
 * I click an appointment, it says load but should say Appt - AXXX"*.
 *
 * ⛔⚤ THE KIND WORD WAS RIGHT WHEN IT SHIPPED and stopped being right the next
 * day. On 2026-09-19 the panel heading said only `Driver Check-Ins`, so `(client)`
 * was the ONLY thing on screen naming the scope. §1AV.174 then put the party on
 * line 1 — and `(client)` became a second, vaguer copy of a fact already stated.
 *   ** A LABEL EARNS ITS PLACE AGAINST WHAT IS ALREADY ON SCREEN, NOT AGAINST
 *      WHAT WAS ON SCREEN WHEN IT WAS WRITTEN. **
 *
 * ⚠️ NOT A THIRD LABELLER. `focusBasisLabel` answers *"what identifies this
 * focus"* inside a sentence; this answers *"what is this panel scoped to"* inside a
 * parenthesis, and it reads the SAME fields in the same order. ⛔ Do not "unify"
 * them by having one call the other — the prefixes and the fallbacks differ, and a
 * shared helper with two flags is two functions wearing one name.
 *
 * ⛔⛔ `driver` IS DELIBERATELY UNCHANGED. Pete specified parties and appointments
 * and said nothing about drivers; §1AV.179 records that as an OPEN GAP rather than
 * filling it in. ⚠️ A driver's name is already on line 1 (`Driver Check-Ins · PETER
 * JOSEPH LOMBARDO`), so repeating it in five chips is the duplication this change
 * exists to remove — but that is an argument, not his answer, and it is not applied.
 *
 * ⚠️ THE FALLBACKS ARE NOT DECORATION. A focus with no number or no label is a
 * focus built wrong; `(appointment)` / `(client)` is what makes that visible instead of
 * rendering `(Appt - )` or an empty pair of brackets.
 */
export function focusScopeLabel(f: RailFocus | null): string {
    if (!f) return '(ALL)';
    if (f.kind === 'appointment') {
        return f.appointmentNumber ? `(Appt - ${f.appointmentNumber})` : `(${SCOPE_WORD.appointment})`;
    }
    /* ✅⚤ ~~THE GAP, §1AV.179~~ — **ANSWERED 2026-09-20: `(Driver · Peter L.)`.**
       ⭐ Pete chose first name + last initial over the full surname: *"it will most likely
       save space and the scheduler will call out the first name."*
       ⚠️ SAME SHAPE AS THE APPOINTMENT CASE — a kind prefix plus a short identifier — so
       the five chips read as one family rather than four unrelated conventions.
       ⚠️ AND IT FALLS BACK TO THE BARE WORD when there is no name to show, which is the
       walk-up case: an arrival with a phone and no licence yet. */
    if (f.kind === 'driver') {
        const who = shortDriverName(f.driverName);
        return who ? `(Driver · ${who})` : `(${SCOPE_WORD.driver})`;
    }
    const who = (f.partyName ?? f.label ?? '').trim() || (f.partyCode ?? '').trim();
    return who ? `(${who})` : `(${SCOPE_WORD[f.kind]})`;
}

export function focusBasisLabel(f: RailFocus): string {
    /* ⛔⚤ ~~if (f.partyName) return f.partyName;~~ — DELETED, same reason. A party
       focus has `appointmentId === null`, so it already falls through to `label`,
       and `focusFromParty` sets `label` to the very same string. The line was a
       second path to one answer.
       ⚠️ THE INVARIANT IT WAS PROTECTING IS REAL and is asserted in
       `rail-focus-test`: a party names itself, never one of its appointments. It holds
       because `appointmentId` is null for a party — which is the line that is
       actually doing the work, two fields up in the constructor. */
    if (f.appointmentId && f.appointmentNumber) return f.appointmentNumber;
    if (f.phoneTail) return `····${tailDigits(f.phoneTail) ?? f.phoneTail}`;
    return f.label;
}
